import { Maximize, Minimize } from 'lucide-react';
import { useState, useEffect, useRef, type ReactNode, type RefObject } from 'react';

export interface FullscreenToggleProps {
    targetRef?: RefObject<HTMLElement | null>;
    className?: string;
    size?: 'sm' | 'md' | 'lg';
    variant?: 'default' | 'minimal';
    children?: ReactNode;
    showLabel?: boolean;
    onFullscreenChange?: (isFullscreen: boolean) => void;
    isFullscreen?: boolean;
    onToggle?: () => void;
}

const FullscreenToggle = ({
    targetRef,
    className = '',
    size = 'md',
    variant = 'default',
    children,
    showLabel = false,
    onFullscreenChange,
    isFullscreen: externalIsFullscreen,
    onToggle: externalOnToggle,
}: FullscreenToggleProps) => {
    const [internalIsFullscreen, setInternalIsFullscreen] = useState(false);
    const defaultTargetRef = useRef<HTMLDivElement>(null);
    const target = targetRef || defaultTargetRef;

    // Use external state if provided, otherwise use internal state
    const isFullscreen = externalIsFullscreen !== undefined ? externalIsFullscreen : internalIsFullscreen;

    // Handle fullscreen toggle
    const toggleFullscreen = async () => {
        // If external toggle is provided, use it
        if (externalOnToggle) {
            externalOnToggle();
            return;
        }

        // Otherwise, use internal logic
        if (!target.current) {
            return;
        }

        try {
            if (!isFullscreen) {
                await target.current.requestFullscreen();
                setInternalIsFullscreen(true);
            } else {
                await document.exitFullscreen();
                setInternalIsFullscreen(false);
            }
        } catch (error) {
            console.error('Fullscreen error:', error);
        }
    };

    // Listen for fullscreen change events (only when using internal state)
    useEffect(() => {
        if (externalIsFullscreen !== undefined) {
            return; // Don't listen for changes when using external state
        }

        const handleFullscreenChange = () => {
            const newFullscreenState = !!document.fullscreenElement;
            setInternalIsFullscreen(newFullscreenState);
            onFullscreenChange?.(newFullscreenState);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, [externalIsFullscreen, onFullscreenChange]);

    // Size classes
    const sizeClasses = {
        sm: 'w-8 h-8',
        md: 'w-10 h-10',
        lg: 'w-12 h-12',
    };

    // Variant classes - updated to match the minimalist design
    const variantClasses = {
        default:
            'bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 hover:scale-105',
        minimal:
            'bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 hover:scale-105',
    };

    // Icon sizes
    const iconSizes = {
        sm: 'h-4 w-4',
        md: 'h-5 w-5',
        lg: 'h-6 w-6',
    };

    // Icon colors
    const iconColor = 'text-gray-700';

    return (
        <button
            onClick={toggleFullscreen}
            className={`flex items-center justify-center ${sizeClasses[size]} ${variantClasses[variant]} ${className}`}
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
            {children || (
                <>
                    {isFullscreen ? (
                        <Minimize className={`${iconSizes[size]} ${iconColor} ${showLabel ? 'mr-2' : ''}`} />
                    ) : (
                        <Maximize className={`${iconSizes[size]} ${iconColor} ${showLabel ? 'mr-2' : ''}`} />
                    )}
                    {showLabel && (
                        <span className="text-sm font-medium text-gray-700">
                            {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                        </span>
                    )}
                </>
            )}
        </button>
    );
};

export default FullscreenToggle;
